import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Headless from '@headlessui/react';

import { Description, Field, Label } from '@oysterjs/ui/fieldset';

import { FileInput, type FileInputChange } from '~/components/file';
import { type UseCaseSpec } from './use-case-catalog';
import { expandLanes } from './run-card-submission';

/** Key lanes by identity so adding/removing a repeater cannot reassign files to another role. */
export const RunCardSources = ({
  spec,
  repeatCount,
  laneKeys,
  onChangeLane,
  onAddLane,
  onRemoveLane
}: {
  spec: UseCaseSpec;
  repeatCount: number;
  laneKeys: string[];
  onChangeLane: (index: number, change: FileInputChange) => void;
  onAddLane: () => void;
  onRemoveLane: (index: number) => void;
}) => {
  const lanes = expandLanes(spec, repeatCount);
  if (lanes.length === 0) {
    return null;
  }

  const repeatable = spec.sources.some((lane) => lane.repeatable);
  const removable = lanes.filter((lane) => lane.repeatable).length > 1;

  return (
    <Field>
      <Label>Sources</Label>
      <div data-slot="control" className="flex flex-col gap-3">
        {lanes.map((lane, index) => (
          <div
            key={laneKeys[index] ?? index}
            role="group"
            // Screen readers get the standing in the group name, since the visual
            // marker alone would not reach them.
            aria-label={lane.requirement ? `${lane.label} (${lane.requirement})` : lane.label}
            className="flex flex-col gap-1"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm/6 font-medium text-neutral-950 dark:text-white">
                {lane.label}
              </span>
              <div className="flex shrink-0 items-center gap-2">
                {/* Right-aligned and lighter so it reads as a status column down the
                    card rather than as part of the lane's name, which is what sank
                    the earlier "(optional)" in the label. Recommended sits a shade
                    darker because it is advice, where optional is only permission. */}
                {lane.requirement && (
                  <span
                    aria-hidden
                    className={
                      lane.requirement === 'recommended'
                        ? 'text-xs text-neutral-500 dark:text-neutral-400'
                        : 'text-xs text-neutral-400 dark:text-neutral-500'
                    }
                  >
                    {lane.requirement === 'recommended' ? 'Recommended' : 'Optional'}
                  </span>
                )}
                {lane.repeatable && removable && (
                  <Headless.Button
                    type="button"
                    aria-label={`Remove ${lane.label}`}
                    onClick={() => onRemoveLane(index)}
                    className="rounded p-0.5 text-neutral-500 dark:text-neutral-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <XMarkIcon className="size-4" />
                  </Headless.Button>
                )}
              </div>
            </div>
            <Description>{lane.description}</Description>
            <FileInput
              compact
              multiple
              enableExisting
              enableAMSSearch
              onChange={(change) => onChangeLane(index, change)}
            />
          </div>
        ))}
        {repeatable && (
          <Headless.Button
            type="button"
            onClick={onAddLane}
            className="inline-flex items-center gap-1 self-start text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <PlusIcon className="size-4" />
            Add an option
          </Headless.Button>
        )}
      </div>
    </Field>
  );
};
